import { useState, useCallback } from "react";
import IspettoriService from "../services/IspettoriService/IspettoriService";
import { alertService } from "../services/AlertService";
import { handleError } from "../model/handleError";

const useIspettori = () => {
  const [loading, setLoading] = useState({
    enti: false,
    ispettori: false,
    corsi: false,
    log: false,
    area: false,
    report: false,
  });

  const findEnti = useCallback(async (params) => {
    setLoading((s) => ({ ...s, enti: true }));
    try {
      const res = await IspettoriService.findEnti(params);
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, enti: false }));
    }
  }, []);

  const indexEnti = useCallback(async () => {
    try {
      return await IspettoriService.indexEnti();
    } catch (e) {
      handleError(e);
      return [];
    }
  }, []);

  const createEnte = useCallback(async (data) => {
    setLoading((s) => ({ ...s, enti: true }));
    try {
      const res = await IspettoriService.createEnte(data);
      alertService.success("Ente ispettivo creato", { autoClose: true });
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, enti: false }));
    }
  }, []);

  const updateEnte = useCallback(async (data) => {
    setLoading((s) => ({ ...s, enti: true }));
    try {
      const res = await IspettoriService.updateEnte(data);
      alertService.success("Ente aggiornato", { autoClose: true });
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, enti: false }));
    }
  }, []);

  const deleteEnte = useCallback(async (id) => {
    setLoading((s) => ({ ...s, enti: true }));
    try {
      await IspettoriService.deleteEnte(id);
      alertService.success("Ente eliminato", { autoClose: true });
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, enti: false }));
    }
  }, []);

  const findIspettori = useCallback(async (params) => {
    setLoading((s) => ({ ...s, ispettori: true }));
    try {
      return await IspettoriService.findIspettori(params);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, ispettori: false }));
    }
  }, []);

  const createIspettore = useCallback(async (data) => {
    setLoading((s) => ({ ...s, ispettori: true }));
    try {
      const res = await IspettoriService.createIspettore(data);
      alertService.success("Ispettore creato. Credenziali inviate via email.", { autoClose: true });
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, ispettori: false }));
    }
  }, []);

  const updateIspettore = useCallback(async (data) => {
    setLoading((s) => ({ ...s, ispettori: true }));
    try {
      const res = await IspettoriService.updateIspettore(data);
      alertService.success("Ispettore aggiornato", { autoClose: true });
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, ispettori: false }));
    }
  }, []);

  const assegnaCorsi = useCallback(async (data) => {
    setLoading((s) => ({ ...s, corsi: true }));
    try {
      const res = await IspettoriService.assegnaCorsi(data);
      alertService.success("Corsi assegnati", { autoClose: true });
      return res;
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, corsi: false }));
    }
  }, []);

  const rimuoviCorso = useCallback(async (id_utente, id_corso) => {
    setLoading((s) => ({ ...s, corsi: true }));
    try {
      await IspettoriService.rimuoviCorso(id_utente, id_corso);
      alertService.success("Corso rimosso dall'ispettore", { autoClose: true });
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, corsi: false }));
    }
  }, []);

  const findCorsiByIspettore = useCallback(async (id_utente) => {
    try {
      return await IspettoriService.findCorsiByIspettore(id_utente);
    } catch (e) {
      handleError(e);
      return [];
    }
  }, []);

  const findAccessLog = useCallback(async (params) => {
    setLoading((s) => ({ ...s, log: true }));
    try {
      return await IspettoriService.findAccessLog(params);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, log: false }));
    }
  }, []);

  const mieCorsi = useCallback(async () => {
    setLoading((s) => ({ ...s, area: true }));
    try {
      return await IspettoriService.mieCorsi();
    } catch (e) {
      handleError(e);
      return [];
    } finally {
      setLoading((s) => ({ ...s, area: false }));
    }
  }, []);

  const corsistiByCorso = useCallback(async (id_corso) => {
    setLoading((s) => ({ ...s, area: true }));
    try {
      return await IspettoriService.corsistiByCorso(id_corso);
    } catch (e) {
      handleError(e);
      return [];
    } finally {
      setLoading((s) => ({ ...s, area: false }));
    }
  }, []);

  const anagraficaCorsista = useCallback(async (id_corso, id_utente_corsista) => {
    try {
      return await IspettoriService.anagraficaCorsista(id_corso, id_utente_corsista);
    } catch (e) {
      handleError(e);
    }
  }, []);

  const percorsoFormativo = useCallback(async (payload) => {
    setLoading((s) => ({ ...s, area: true }));
    try {
      return await IspettoriService.percorsoFormativo(payload);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, area: false }));
    }
  }, []);

  const downloadActivityReport = useCallback(async (id_corso, id_utente_corsista) => {
    setLoading((s) => ({ ...s, report: true }));
    try {
      await IspettoriService.downloadActivityReport(id_corso, id_utente_corsista);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading((s) => ({ ...s, report: false }));
    }
  }, []);

  return {
    loadingIspettori: loading,
    findEnti,
    indexEnti,
    createEnte,
    updateEnte,
    deleteEnte,
    findIspettori,
    createIspettore,
    updateIspettore,
    assegnaCorsi,
    rimuoviCorso,
    findCorsiByIspettore,
    findAccessLog,
    mieCorsi,
    corsistiByCorso,
    anagraficaCorsista,
    percorsoFormativo,
    downloadActivityReport,
  };
};

export default useIspettori;
